import { AbstractControl } from 'ngx-strongly-typed-forms';
import {
  AbstractControl as AbstractControlAlt,
  FormArray,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';
import { MetaDataField } from '../../infrastructure/classes/meta-data-field';
import { isNil } from 'lodash';
import { EMAIL_NO_SPECIAL_CHARS_PATTERN, EMAIL_PATTERN } from '../../constants/patterns.constant';

type ValidationErrors = { [key: string]: boolean } | null;

export const CustomValidators = {
  required: (control: AbstractControl<string>): ValidationErrors =>
    !control?.value?.trim()?.length ? { required: true } : null,

  passwordMisMatch: <T>(
    passwordPath: keyof T,
    confirmPasswordPath: keyof T
  ) => {
    return (control: AbstractControl<T>): ValidationErrors => {
      const password = control.get(passwordPath);
      const confirmPassword = control.get(confirmPasswordPath);
      const isMismatched =
        confirmPassword.value &&
        password.value &&
        confirmPassword.value !== password.value;

      if (isMismatched) {
        confirmPassword.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
      }

      if (confirmPassword.value) {
        confirmPassword.setErrors(null);
      }
      return null;
    };
  },

  validEmail: (control: AbstractControl<string>): ValidationErrors =>
    !control?.value?.match(EMAIL_PATTERN) // NOSONAR
      ? { validEmail: true }
      : null,

  emailHasNoSpecialChars: (control: AbstractControl<string>): ValidationErrors =>
    !control?.value?.match(EMAIL_NO_SPECIAL_CHARS_PATTERN) // NOSONAR
      ? { emailHasNoSpecialChars: true }
      : null,

  atLeast1SpecialChar: (control: AbstractControl<string>): ValidationErrors =>
    !control?.value?.match(/(?=.*[-+_!@#$%^&*?]).*/) // NOSONAR
      ? { specialChar: true }
      : null,

  lessThanOrEqual: (
    aName: string,
    bName: string,
    metaData: MetaDataField[]
  ) => {
    return (control: AbstractControlAlt): ValidationErrors => {
      const a = control.get(aName);
      const b = control.get(bName);
      const isLessThanOrEqual = Number(a.value) <= Number(b.value);

      if (!isLessThanOrEqual) {
        const aFieldName = metaData.find(
          (item) => item.field === aName
        ).displayName;
        const bFieldName = metaData.find(
          (item) => item.field === bName
        ).displayName;

        a.setErrors({
          lessThanOrEqual: `${aFieldName} must be less than or equal to ${bFieldName}`,
        });
        return { lessThanOrEqual: true };
      }

      if (a.value) {
        a.errors && delete a.errors.lessThanOrEqual;
        a.setErrors(Object.keys(a.errors || {}).length ? a.errors : null);
      }
      return a.errors;
    };
  },

  greaterThanOrEqual: (
    aName: string,
    bName: string,
    metaData: MetaDataField[]
  ) => {
    return (control: AbstractControlAlt): ValidationErrors => {
      const a = control.get(aName);
      const b = control.get(bName);
      const isGreaterThanOrEqual = Number(a.value) >= Number(b.value);
      if (!isGreaterThanOrEqual) {
        const aFieldName = metaData.find(
          (item) => item.field === aName
        ).displayName;
        const bFieldName = metaData.find(
          (item) => item.field === bName
        ).displayName;

        a.setErrors({
          greaterThanOrEqual: `${aFieldName} must be greater than or equal to ${bFieldName}`,
        });
        return { greaterThanOrEqual: true };
      }

      if (a.value) {
        a.errors && delete a.errors.greaterThanOrEqual;
        a.setErrors(Object.keys(a.errors || {}).length ? a.errors : null);
      }
      return a.errors;
    };
  },

  lessThanOrEqualStatic: (
    aName: string,
    bName: string,
    bValue: number,
    metaData: MetaDataField[]
  ) => {
    return (control: AbstractControlAlt): ValidationErrors => {
      const a = control.get(aName);

      if (!a) {
        return null;
      }

      const isLessThanOrEqual = Number(a.value) <= bValue;

      if (!isLessThanOrEqual) {
        const aFieldName = metaData.find(
          (item) => item.field === aName
        ).displayName;
        const bFieldName = metaData.find(
          (item) => item.field === bName
        ).displayName;

        a.setErrors({
          lessThanOrEqualStatic: `${aFieldName} must be less than or equal to ${bFieldName}`,
        });

        return { lessThanOrEqualStatic: true };
      }

      if (a.value) {
        a.errors && delete a.errors.lessThanOrEqualStatic;
        a.setErrors(Object.keys(a.errors || {}).length ? a.errors : null);
      }
      return a.errors;
    };
  },

  divisibleBy: (aName: string, bName: string, metaData: MetaDataField[]) => {
    return (control: AbstractControlAlt): ValidationErrors => {
      const a = control.get(aName);
      const b = control.get(bName);
      const isDivisibleBy =
        Number(a.value) % Number(b.value) === 0 || Number(b.value) === 0;

      if (!isDivisibleBy) {
        const aFieldName = metaData.find(
          (item) => item.field === aName
        ).displayName;
        const bFieldName = metaData.find(
          (item) => item.field === bName
        ).displayName;

        a.setErrors({
          divisibleBy: `${aFieldName} must be divisible by ${bFieldName}`,
        });
        b.setErrors({
          divisibleBy: `${aFieldName} must be divisible by ${bFieldName}`,
        });
        return { divisibleBy: true };
      }

      if (b.value) {
        b.errors && delete b.errors.divisibleBy;
        b.setErrors(Object.keys(b.errors || {}).length ? b.errors : null);
      }
      return b.errors;
    };
  },

  casePackStateUnchanged: () => {
    return (control: AbstractControlAlt): ValidationErrors => {
      const caseQty = control.get('caseQty');
      const isShipByCase = control.get('isShipByCase');
      const shipmentId = control.get('shipmentId');

      if (!shipmentId.value) {
        return null;
      }

      if (
        (caseQty.value > 0 && !isShipByCase.value) ||
        (!caseQty.value && isShipByCase.value)
      ) {
        caseQty.setErrors({
          casePackStateUnchanged:
            'You cannot change the case pack state after shipment had been uploaded to Amazon',
        });
        return { casePackStateUnchanged: true };
      }

      caseQty.errors && delete caseQty.errors.casePackStateUnchanged;
      caseQty.setErrors(
        Object.keys(caseQty.errors || {}).length ? caseQty.errors : null
      );
      return caseQty.errors;
    };
  },

  totalShipmentQtyMustBeEqualTo(expectTotals: number[]): ValidatorFn {
    return (boxesFormGroup: FormArray) => {
      const actualTotals = expectTotals.map(() => 0);
      boxesFormGroup.controls.forEach((boxGroup) => {
        const boxItemsFormGroup = boxGroup.get('boxItems') as FormArray;
        boxItemsFormGroup.controls.forEach((boxItemGroup, itemIdx) => {
          actualTotals[itemIdx] += boxItemGroup.get('shipmentQty').value;
        });
      });

      const isValid = expectTotals
        ?.map((t, idx) => t === actualTotals[idx])
        ?.every((t) => t);

      return isValid
        ? null
        : {
            error:
              'The quantity in each box does amount to the total shipment quantity.',
          };
    };
  },

  totalBoxCountMustBeLessThanOrEqualTo(total: number) {
    return (boxesFormGroup: FormArray) => {
      let boxCount = 0;
      boxesFormGroup.controls.forEach((boxGroup) => {
        const count = boxGroup.get('count')?.value;
        boxCount += count || 0;
      });

      return boxCount <= total
        ? null
        : {
            error: `Box count (${boxCount}) must be less than or equal to the total shipment quantity (${total}).`,
          };
    };
  },

  sumControlsLessThanAmount(
    controlNames: string[],
    maxAmount: number,
    metadataFields?: MetaDataField[]
  ): ValidatorFn {
    return (formGroup: AbstractControlAlt) => {
      // Calculate sum of specified control values
      let controlNamesString = '';
      if (!metadataFields) {
        controlNamesString = controlNames.join(', ');
      } else {
        controlNamesString = metadataFields
          .filter((m) => controlNames.includes(m.field))
          .map((m) => m.displayName)
          .join(', ');
      }

      let sum = 0;
      for (const controlName of controlNames) {
        const control = formGroup.get(controlName);

        if (control) {
          const value = Number(control.value) || 0;
          sum += value;
        }
      }

      controlNames.forEach((controlName) => {
        const control = formGroup.get(controlName);
        if (control && !isNil(control.value) && sum > maxAmount) {
          control.setErrors({
            sumLessThanAmount: `The sum of ${controlNamesString} must be less than ${maxAmount}`,
          });
        } else {
          control.setErrors(null);
        }
      });
      return null;
    };
  },

  passwordConfirm(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];
      if (
        matchingControl.errors &&
        !matchingControl.errors.confirmedValidator
      ) {
        return;
      }
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ confirmedValidator: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }
};
